import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16, IcoChevronRight16, IcoFolders16 } from '@onefootprint/icons';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { cx } from 'class-variance-authority';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import useChatMessages from '../../hooks/use-chat-messages';
import type { ChatMessage, PendingMessage, SendMessageArgs, UserMessage } from '../../hooks/use-chat-ws';
import useDocumentsPanelOpen from '../../hooks/use-documents-panel-open';
import { useResourceContextSelection } from '../../hooks/use-resource-context-selection';
import { useSuggestedActions } from '../../hooks/use-suggested-actions';
import { activeWalkthrough, finishedWalkthroughs, walkthroughEndMessage } from '../../utils/active-walkthrough';
import { agentSuggestionsFromGroup, buildSegments, coalesceJourneyBatches, hiddenBrowserSessions, isAgentSuggestionGroupKey, isPollOnlyToolGroup, isResourceCreateMessage, resourceCreationToolsWithCard, type Segment, summarizeResourceCreatedGroup, supersededAgentSuggestions } from '../../utils/chat-segments';
import { resourceKey } from '../../utils/resource-presentation';
import { hasVisibleContent } from '../../utils/tool-call-summary';
import type { AskUserQuestionInput } from '../ask-user-question-flow';
import BrowserRun from '../browser-run';
import ChatInput, { type ChatInputHandle, ChatInputMentionContext, type MentionAttrs } from '../chat-input';
import ChatMessageItem, { ChatTranscriptReadOnlyContext } from '../chat-message';
import { deriveBackgroundAgents } from '../chat-widget/background-agents-context';
import { composerActions } from '../composer-actions';
import ComposerDisclaimer from '../composer-disclaimer';
import JourneyBatchCard from '../journey-batch-card';
import JourneyCard from '../journey-card';
import NotificationsNudge from '../notifications-nudge';
import { PageLinksInPlaceContext } from '../page-link';
import PinnedStack, { hasPinnedContent, PINNED_SCROLLBACK_CLASSNAME, PINNED_SCROLLPORT_CLASSNAME } from '../pinned-stack';
import ResourceCreatedCard, { AgentSuggestionsCard } from '../resource-created-card';
import RunningAgentsIndicator from '../running-agents-indicator';
import SuggestedActions from '../suggested-actions';
import ThinkingIndicator from '../thinking-indicator';
import { ToolCallGroup } from '../tool-call-group';
import WalkthroughEvent from '../walkthrough-event';
import WalkthroughHeader from '../walkthrough-header';

// A consecutive run of resource-created cards. One renders bare; two or more join into a single
// block under a count header — the header is a peer row (same gray fill) with the summary and a
// far-right disclosure chevron. Only the outer corners round and rows sit flush, so the burst
// reads as one unit; the user can fold it down to just the header.
type ResourceCreatedGroupProps = {
  messages: ChatMessage[];
  session: ChatSessionResponse;
};
const ResourceCreatedGroup = t0 => {
  const $ = _c(9);
  const {
    messages,
    session
  } = t0;
  const [collapsed, setCollapsed] = useState(false);
  let t1;
  let t2;
  if ($[0] !== collapsed || $[1] !== messages || $[2] !== session) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resources = messages.filter(isResourceCreateMessage);
      if (resources.length === 0) {
        t2 = null;
        break bb0;
      }
      if (resources.length === 1) {
        t2 = <ChatMessageItem message={resources[0]} session={session} />;
        break bb0;
      }
      let t3;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = () => setCollapsed(_temp);
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      let t4;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <span className="shrink-0 flex items-center justify-center size-6 text-primary"><IcoFolders16 /></span>;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      let t5;
      if ($[7] !== collapsed) {
        t5 = <span className="shrink-0 size-7 flex items-center justify-center text-tertiary transition group-hover:text-primary">{collapsed ? <IcoChevronRight16 /> : <IcoChevronDown16 />}</span>;
        $[7] = collapsed;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      t1 = <div className="flex flex-col"><button type="button" onClick={t3} aria-expanded={!collapsed} className={`group flex items-center gap-1 w-full pl-2 pr-1.5 bg-secondary transition-colors cursor-pointer ${collapsed ? "rounded-default hover:bg-hover" : "rounded-t-default"}`}>{t4}<span className="flex-1 min-w-0 py-2 text-label-3 text-primary truncate text-left">{summarizeResourceCreatedGroup(resources)}</span>{t5}</button>{!collapsed && <div className="relative flex flex-col"><span aria-hidden={true} className="pointer-events-none absolute left-[19px] top-0 bottom-3 border-l border-tertiary" />{resources.map((message, i) => <ResourceCreatedCard key={message.resource ? resourceKey(message.resource) : i} message={message} position={i === resources.length - 1 ? "last" : "middle"} indented={true} />)}</div>}</div>;
    }
    $[0] = collapsed;
    $[1] = messages;
    $[2] = session;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return t1;
};

// Segments render untyped wire data; a throw during render must degrade one row to a quiet
// notice, not blank the conversation. Mirrors `gen-ui-block-renderer`'s ErrorBoundary.
const SegmentBoundary = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="text-caption-2 text-tertiary px-1">Couldn't display this step.</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <ErrorBoundary fallback={t1} onError={_temp2}>{children}</ErrorBoundary>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
type ChatPanelProps = {
  messages: ChatMessage[];
  pendingMessages?: PendingMessage[];
  session: ChatSessionResponse;
  isConnected: boolean;
  isThinking: boolean;
  isConnecting?: boolean;
  onSendMessage: (content: string, args: SendMessageArgs) => Promise<void>;
  onInterrupt: (options?: {
    silent?: boolean;
  }) => void;
  onApproval?: (approvalId: string, approved: boolean, approveAll?: boolean) => void;
  /** Cancel a still-queued pending message by its client-generated id. */
  onCancelPending?: (id: string) => void;
  forceScrollToBottom?: boolean;
  /** Override the default localized placeholder. Used by Percy builder. */
  placeholderOverride?: string;
  /** Default true. Set false to hide the paperclip and disable drag-drop (Percy builder). */
  enableAttachments?: boolean;
  /**
   * Sandbox state for the session. When set, the composer shows a locked
   * environment selector; omit while the session's env is still loading.
   */
  isLive?: boolean;
  /** A finished autopilot transcript: shows an "ended" marker and a new-conversation placeholder. */
  isAutopilotEndedConversation?: boolean;
  /** Read-only transcript mode: renders the message list without the composer. */
  readOnly?: boolean;
  /** The live browser row pinned above the composer while the pane is not showing. */
  pinnedBrowser?: React.ReactNode;
  /**
   * Opens a sub-agent's transcript. Defaults to the chat widget's agents sidebar; a transcript shown
   * in a modal passes its own, since the sidebar sits behind the modal.
   */
  onOpenAgent?: (spawnId: string) => void;
};

// How long the active AskUserQuestion input must hold steady before we treat
// it as stable enough to pin. The Claude Agent SDK streams the tool_use input
// JSON token by token, so the `questions[].options` array grows over several
// frames; without this debounce the card would flash through partial inputs.
const STABLE_WINDOW_MS = 600;
const ChatPanel = t0 => {
  const $ = _c(87);
  const {
    messages,
    pendingMessages: t1,
    session,
    isConnected,
    isThinking,
    isConnecting,
    onSendMessage,
    onInterrupt,
    onApproval,
    onCancelPending,
    forceScrollToBottom,
    placeholderOverride,
    enableAttachments: t2,
    isLive,
    isAutopilotEndedConversation,
    readOnly: t3,
    pinnedBrowser,
    onOpenAgent
  } = t0;
  const pendingMessages = t1 === undefined ? [] : t1;
  const enableAttachments = t2 === undefined ? true : t2;
  const readOnly = t3 === undefined ? false : t3;
  const {
    t
  } = useTranslation("copilot");
  const {
    effectiveContext
  } = useResourceContextSelection();
  const suggestedActions = useSuggestedActions();
  let t4;
  if ($[0] !== isLive || $[1] !== t) {
    t4 = composerActions({
      env: isLive === undefined ? undefined : {
        value: isLive ? "production" : "sandbox",
        lockedTooltip: t(_temp3)
      }
    });
    $[0] = isLive;
    $[1] = t;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const actions = t4;
  const {
    openAgent: openInSidebar
  } = useDocumentsPanelOpen();
  const openAgent = onOpenAgent ?? openInSidebar;
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const scrollRef = useRef(null);
  const contentWrapRef = useRef(null);
  const atBottomRef = useRef(true);
  const initialScrollDoneRef = useRef(false);
  const smoothScrollOnNextEffectRef = useRef(false);
  const inputRef = useRef(null);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      mentionEntity: attrs => inputRef.current?.mentionEntity(attrs)
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const mentionContext = t5;
  let t6;
  if ($[4] !== messages) {
    t6 = {
      messages
    };
    $[4] = messages;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const {
    toolResults,
    toolErrors,
    approvalToolResults,
    questionAnswerMessages,
    visibleMessages,
    latestUnansweredQuestionId,
    latestTodos,
    resolvedApprovalIds
  } = useChatMessages(t6);
  const walkthrough = activeWalkthrough(messages, toolResults, pendingMessages);
  const finishedWalkthroughTitles = finishedWalkthroughs(messages, toolResults);
  const hasPendingAnswer = !!latestUnansweredQuestionId && pendingMessages.some(m => m.question_id === latestUnansweredQuestionId);
  const hasUnansweredQuestion = !!latestUnansweredQuestionId && !hasPendingAnswer;
  const answersByQuestionId = {
    ...questionAnswerMessages
  };
  for (const p of pendingMessages) {
    if (p.question_id && !answersByQuestionId[p.question_id]) {
      answersByQuestionId[p.question_id] = p as unknown as UserMessage;
    }
  }
  let t7;
  if ($[6] !== messages) {
    t7 = messages.some(_temp4);
    $[6] = messages;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const hasReceivedTurnComplete = t7;
  const isDecisionInputLocked = "agentKind" in session && session.agentKind === "decision" && !hasReceivedTurnComplete;
  let t8;
  if ($[8] !== messages) {
    t8 = messages.reduce(_temp5, 0);
    $[8] = messages;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const turnIndex = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const atBottom = distanceFromBottom < 150;
      atBottomRef.current = atBottom;
      setIsScrolledUp(!atBottom);
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleScroll = t9;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const el_0 = scrollRef.current;
      if (!el_0) {
        return;
      }
      el_0.scrollTo({
        top: el_0.scrollHeight,
        behavior: "smooth"
      });
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const scrollToBottom = t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      const el_1 = scrollRef.current;
      if (el_1) {
        el_1.scrollTop = el_1.scrollHeight;
      }
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const jumpToBottom = t11;
  const endWalkthrough = () => {
    if (!walkthrough) {
      return;
    }
    if (isThinking) {
      onInterrupt({
        silent: true
      });
    }
    smoothScrollOnNextEffectRef.current = true;
    onSendMessage(walkthroughEndMessage(walkthrough), {
      files: [],
      context: effectiveContext
    });
  };
  let t12;
  if ($[13] !== latestUnansweredQuestionId || $[14] !== onSendMessage) {
    t12 = async (content, args) => {
      const effectiveArgs = !args.questionId && latestUnansweredQuestionId ? {
        ...args,
        questionId: latestUnansweredQuestionId
      } : args;
      smoothScrollOnNextEffectRef.current = true;
      await onSendMessage(content, effectiveArgs);
    };
    $[13] = latestUnansweredQuestionId;
    $[14] = onSendMessage;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const handleSendMessage = t12;
  let t13;
  if ($[16] !== messages.length) {
    t13 = () => {
      if (initialScrollDoneRef.current) {
        return;
      }
      const el_2 = scrollRef.current;
      if (!el_2 || messages.length === 0) {
        return;
      }
      initialScrollDoneRef.current = true;
      jumpToBottom();
    };
    $[16] = messages.length;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== messages) {
    t14 = [messages, jumpToBottom];
    $[18] = messages;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  useLayoutEffect(t13, t14);
  let t15;
  if ($[20] !== scrollToBottom) {
    t15 = () => {
      const el_3 = scrollRef.current;
      if (!el_3 || !initialScrollDoneRef.current) {
        return;
      }
      const distanceFromBottom_0 = el_3.scrollHeight - el_3.scrollTop - el_3.clientHeight;
      if (distanceFromBottom_0 < 150) {
        if (smoothScrollOnNextEffectRef.current) {
          smoothScrollOnNextEffectRef.current = false;
          scrollToBottom();
        } else {
          jumpToBottom();
        }
      }
    };
    $[20] = scrollToBottom;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== isThinking || $[23] !== messages || $[24] !== scrollToBottom) {
    t16 = [messages, isThinking, jumpToBottom, scrollToBottom];
    $[22] = isThinking;
    $[23] = messages;
    $[24] = scrollToBottom;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[26] !== forceScrollToBottom) {
    t17 = () => {
      if (!forceScrollToBottom) {
        return;
      }
      const timer = setTimeout(jumpToBottom, 550);
      return () => clearTimeout(timer);
    };
    t18 = [forceScrollToBottom, jumpToBottom];
    $[26] = forceScrollToBottom;
    $[27] = t17;
    $[28] = t18;
  } else {
    t17 = $[27];
    t18 = $[28];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      const wrap = contentWrapRef.current;
      const el_4 = scrollRef.current;
      if (!wrap || !el_4) {
        return;
      }
      const ro = new ResizeObserver(() => {
        if (atBottomRef.current) {
          el_4.scrollTop = el_4.scrollHeight;
        }
      });
      ro.observe(wrap);
      ro.observe(el_4);
      return () => ro.disconnect();
    };
    t20 = [];
    $[29] = t19;
    $[30] = t20;
  } else {
    t19 = $[29];
    t20 = $[30];
  }
  useEffect(t19, t20);
  const plainMessages = visibleMessages.map(_temp6);
  const resourceToolsWithCard = resourceCreationToolsWithCard(plainMessages);
  const hiddenSuggestions = supersededAgentSuggestions(plainMessages);
  const hiddenBrowserFrames = hiddenBrowserSessions(plainMessages);
  const segments = coalesceJourneyBatches(buildSegments(visibleMessages.filter(t21 => {
    const {
      msg: msg_0
    } = t21;
    return !(msg_0.type === "tool_use" && resourceToolsWithCard.has(msg_0.id)) && !hiddenSuggestions.has(msg_0) && !hiddenBrowserFrames.has(msg_0);
  }), isThinking && pendingMessages.length === 0));
  const activeQuestionMessage = latestUnansweredQuestionId ? messages.find(m_2 => m_2.type === "tool_use" && m_2.tool === "AskUserQuestion" && m_2.id === latestUnansweredQuestionId) : undefined;
  const activeQuestionInputSignature = activeQuestionMessage?.type === "tool_use" ? JSON.stringify(activeQuestionMessage.input) : null;
  const [stableQuestionSignature, setStableQuestionSignature] = useState(null);
  useEffect(() => {
    if (activeQuestionInputSignature == null) {
      setStableQuestionSignature(null);
      return;
    }
    if (stableQuestionSignature === activeQuestionInputSignature) {
      return;
    }
    const t_0 = setTimeout(() => setStableQuestionSignature(activeQuestionInputSignature), STABLE_WINDOW_MS);
    return () => clearTimeout(t_0);
  }, [activeQuestionInputSignature, stableQuestionSignature]);
  const isQuestionInputStable = stableQuestionSignature !== null && stableQuestionSignature === activeQuestionInputSignature;
  let t22;
  bb0: {
    if (activeQuestionMessage?.type !== "tool_use") {
      t22 = false;
      break bb0;
    }
    const qs = activeQuestionMessage.input.questions as AskUserQuestionInput[] ?? [];
    t22 = qs.some(_temp7);
  }
  const activeQuestionHasOptions = t22;
  const debouncedActiveQuestion = isQuestionInputStable && activeQuestionHasOptions && activeQuestionMessage && activeQuestionMessage.type === "tool_use" ? activeQuestionMessage : undefined;
  const hasIncompleteTodos = latestTodos.length > 0 && latestTodos.some(_temp8);
  const map = {};
  for (const m_3 of messages) {
    if (m_3.type === "tool_use" && m_3.tool === "AskUserQuestion") {
      map[m_3.id] = m_3.input.questions as AskUserQuestionInput[] ?? [];
    }
  }
  const questionsById = map;
  let t23;
  bb1: {
    for (let i = segments.length - 1; i >= 0; i--) {
      const s = segments[i];
      if (s.kind === "message" && s.message.type === "text") {
        t23 = s.id;
        break bb1;
      }
    }
    t23 = undefined;
  }
  const latestTextSegmentId = t23;
  pendingMessages.some(_temp9);
  const lastSegment = segments[segments.length - 1];
  const lastSegmentIsThinking = lastSegment?.kind === "message" && lastSegment.message.type === "thinking" || isPollOnlyToolGroup(lastSegment);
  const hasPendingAnswerForActiveQuestion = !!debouncedActiveQuestion && pendingMessages.some(p_0 => p_0.question_id === debouncedActiveQuestion.id);
  const pinnedQuestion = hasPendingAnswerForActiveQuestion ? undefined : debouncedActiveQuestion;
  const hasPinnedCards = hasPinnedContent({
    showTodos: hasIncompleteTodos,
    question: pinnedQuestion,
    browser: pinnedBrowser
  });
  let t24;
  if ($[31] !== messages) {
    t24 = deriveBackgroundAgents(messages);
    $[31] = messages;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  const backgroundAgents = t24;
  const agentsInPlanCard = backgroundAgents.items.length > 0 && hasIncompleteTodos;
  const showThinking = isConnecting || isThinking;
  const inputPlaceholder = isDecisionInputLocked ? t(_temp10) : hasUnansweredQuestion ? activeQuestionHasOptions ? t(_temp11) : t(_temp12) : isAutopilotEndedConversation ? t(_temp13) : t(_temp14);
  const renderSegment = seg => {
    if (seg.kind === "journey_group") {
      return <JourneyCard group={seg} session={session} variant="reference" onOpen={() => openAgent(seg.spawnId)} />;
    }
    if (seg.kind === "journey_batch") {
      return <JourneyBatchCard batch={seg} onOpenAgent={openAgent} />;
    }
    if (seg.kind === "browser_run") {
      return <BrowserRun segment={seg} toolResults={toolResults} toolErrors={toolErrors} session={session} />;
    }
    if (seg.kind === "tool_group") {
      return <ToolCallGroup messages={seg.messages} toolResults={toolResults} toolErrors={toolErrors} session={session} isThinking={isThinking} />;
    }
    if (seg.kind === "resource_group") {
      if (isAgentSuggestionGroupKey(seg.groupKey)) {
        const {
          agentId,
          count,
          summaries
        } = agentSuggestionsFromGroup(seg.messages);
        return <AgentSuggestionsCard agentId={agentId} count={count} summaries={summaries} session={session} />;
      }
      return <ResourceCreatedGroup messages={seg.messages} session={session} />;
    }
    return <ChatMessageItem message={seg.message} onSendMessage={onSendMessage} session={session} isLive={isLive} questionAnswerMessages={answersByQuestionId} questionsById={questionsById} latestUnansweredQuestionId={latestUnansweredQuestionId} latestTodos={latestTodos} isLatest={!showThinking && seg.id === latestTextSegmentId} onApproval={onApproval} toolResult={seg.message.type === "tool_use" ? toolResults[seg.message.id] : undefined} toolResultIsError={seg.message.type === "tool_use" ? !!toolErrors[seg.message.id] : undefined} approvalToolResult={seg.message.type === "approval_required" ? approvalToolResults[seg.message.approval_id] : undefined} approvalResolved={seg.message.type === "approval_required" ? resolvedApprovalIds.has(seg.message.approval_id) : undefined} />;
  };
  const t25 = ChatInputMentionContext;
  const t26 = ChatTranscriptReadOnlyContext;
  const t27 = PageLinksInPlaceContext;
  const t28 = !!walkthrough && !readOnly;
  const t29 = `relative flex flex-col ${readOnly ? "" : "h-full"}`;
  const t30 = !readOnly && <WalkthroughHeader title={walkthrough?.title} onEnd={endWalkthrough} />;
  const t31 = true;
  const t32 = readOnly ? "" : PINNED_SCROLLPORT_CLASSNAME;
  const t33 = cx("max-w-[820px] w-full mx-auto flex flex-col gap-3", {
    "px-4 md:px-8 pt-8": readOnly,
    [PINNED_SCROLLBACK_CLASSNAME]: !readOnly,
    "max-md:pt-16 max-md:[--pinned-top-inset:4rem]": !readOnly && !!walkthrough,
    "pb-8": readOnly || !hasPinnedCards,
    "min-h-full": !readOnly && hasPinnedCards
  });
  const t34 = segments.map((seg_0, i_0) => {
    if (seg_0.kind === "message" && seg_0.message.type === "tool_use" && seg_0.message.tool === "AskUserQuestion") {
      const qs_0 = seg_0.message.input.questions as AskUserQuestionInput[] ?? [];
      const hasOptions = qs_0.some(_temp15);
      if (hasOptions && !answersByQuestionId[seg_0.message.id]) {
        return null;
      }
    }
    if (seg_0.kind === "message" && seg_0.message.type === "user" && seg_0.message.question_id && (questionsById[seg_0.message.question_id]?.some(_temp16) ?? false)) {
      return null;
    }
    if (seg_0.kind === "message" && seg_0.message.type === "tool_use" && seg_0.message.tool === "TodoWrite" && hasIncompleteTodos) {
      return null;
    }
    if (seg_0.kind === "tool_group" && !seg_0.messages.some(m_5 => hasVisibleContent(m_5, isThinking))) {
      return null;
    }
    const startsNewTurn = i_0 > 0 && seg_0.kind === "message" && seg_0.message.type === "user";
    const segMessages = seg_0.kind === "tool_group" ? seg_0.messages : seg_0.kind === "message" ? [seg_0.message] : [];
    const finishedTitle = segMessages.map(m_6 => m_6.type === "tool_use" ? finishedWalkthroughTitles[m_6.id] : undefined).find(Boolean);
    return <div key={seg_0.id} className={startsNewTurn ? "mt-3" : undefined}><SegmentBoundary>{renderSegment(seg_0)}</SegmentBoundary>{finishedTitle && <div className="mt-6"><WalkthroughEvent event="finished" title={finishedTitle} /></div>}</div>;
  });
  const t35 = pendingMessages.map(msg_1 => {
    if (msg_1.question_id && (questionsById[msg_1.question_id]?.some(_temp17) ?? false)) {
      return null;
    }
    return <SegmentBoundary key={`pending-${msg_1.id || msg_1.content}`}><ChatMessageItem message={msg_1} session={session} questionsById={questionsById} onCancelPending={onCancelPending} /></SegmentBoundary>;
  });
  let t36;
  if ($[33] !== lastSegmentIsThinking || $[34] !== showThinking || $[35] !== turnIndex) {
    t36 = showThinking && !lastSegmentIsThinking && <ThinkingIndicator key={turnIndex} />;
    $[33] = lastSegmentIsThinking;
    $[34] = showThinking;
    $[35] = turnIndex;
    $[36] = t36;
  } else {
    t36 = $[36];
  }
  let t37;
  if ($[37] !== isAutopilotEndedConversation || $[38] !== t) {
    t37 = isAutopilotEndedConversation && <div className="flex items-center gap-3 mt-3" role="status"><span className="flex-1 h-px bg-secondary" /><span className="text-caption-2 text-tertiary whitespace-nowrap">{t(_temp18)}</span><span className="flex-1 h-px bg-secondary" /></div>;
    $[37] = isAutopilotEndedConversation;
    $[38] = t;
    $[39] = t37;
  } else {
    t37 = $[39];
  }
  const t38 = !readOnly && <PinnedStack todos={latestTodos} showTodos={hasIncompleteTodos} question={pinnedQuestion} browser={pinnedBrowser} answersByQuestionId={answersByQuestionId} onSendMessage={onSendMessage} agentsFooter={agentsInPlanCard ? <RunningAgentsIndicator variant="footer" agents={backgroundAgents} /> : undefined} />;
  let t39;
  if ($[40] !== t33 || $[41] !== t34 || $[42] !== t35 || $[43] !== t36 || $[44] !== t37 || $[45] !== t38) {
    t39 = <div ref={contentWrapRef} className={t33}>{t34}{t35}{t36}{t37}{t38}</div>;
    $[40] = t33;
    $[41] = t34;
    $[42] = t35;
    $[43] = t36;
    $[44] = t37;
    $[45] = t38;
    $[46] = t39;
  } else {
    t39 = $[46];
  }
  let t40;
  if ($[47] !== handleScroll || $[48] !== t32 || $[49] !== t39) {
    t40 = <div ref={scrollRef} data-chat-scroll={t31} onScroll={handleScroll} className={t32}>{t39}</div>;
    $[47] = handleScroll;
    $[48] = t32;
    $[49] = t39;
    $[50] = t40;
  } else {
    t40 = $[50];
  }
  let t41;
  if ($[51] !== actions || $[52] !== agentsInPlanCard || $[53] !== backgroundAgents || $[54] !== effectiveContext || $[55] !== enableAttachments || $[56] !== handleSendMessage || $[57] !== hasPinnedCards || $[58] !== inputPlaceholder || $[59] !== isConnected || $[60] !== isDecisionInputLocked || $[61] !== isLive || $[62] !== isScrolledUp || $[63] !== isThinking || $[64] !== onInterrupt || $[65] !== placeholderOverride || $[66] !== readOnly || $[67] !== scrollToBottom || $[68] !== suggestedActions) {
    t41 = !readOnly && <div className="relative px-3 pb-[max(1rem,env(safe-area-inset-bottom))] group-data-[keyboard-open]/app:pb-3 flex flex-col items-center max-w-[820px] w-full mx-auto">{!hasPinnedCards && <div className="absolute -top-6 left-0 right-0 h-6 bg-gradient-to-t from-primary to-transparent pointer-events-none" />}{isScrolledUp && !hasPinnedCards && <button type="button" className="absolute -top-[52px] left-1/2 -translate-x-1/2 w-8 h-8 flex items-center justify-center rounded-full border border-tertiary bg-elevated shadow-sm hover:bg-hover-control transition-colors cursor-pointer z-10" onClick={scrollToBottom} aria-label="Scroll to bottom"><IcoChevronDown16 /></button>}<div className="flex flex-col w-full gap-2">{!agentsInPlanCard && <RunningAgentsIndicator agents={backgroundAgents} />}<NotificationsNudge isThinking={isThinking} /><SuggestedActions actions={suggestedActions} disabled={!isConnected || isDecisionInputLocked || isThinking} onSelect={prompt => handleSendMessage(prompt, {
          files: [],
          context: effectiveContext
        })} scrollerClassName="-mx-3 px-3" /><ChatInput ref={inputRef} placeholder={placeholderOverride ?? inputPlaceholder} isThinking={isThinking} onSendMessage={handleSendMessage} disabled={!isConnected || isDecisionInputLocked} isLive={isLive} onInterrupt={onInterrupt} enableAttachments={enableAttachments} rows={2} {...actions} /></div><ComposerDisclaimer className="w-full" /></div>;
    $[51] = actions;
    $[52] = agentsInPlanCard;
    $[53] = backgroundAgents;
    $[54] = effectiveContext;
    $[55] = enableAttachments;
    $[56] = handleSendMessage;
    $[57] = hasPinnedCards;
    $[58] = inputPlaceholder;
    $[59] = isConnected;
    $[60] = isDecisionInputLocked;
    $[61] = isLive;
    $[62] = isScrolledUp;
    $[63] = isThinking;
    $[64] = onInterrupt;
    $[65] = placeholderOverride;
    $[66] = readOnly;
    $[67] = scrollToBottom;
    $[68] = suggestedActions;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== t29 || $[71] !== t30 || $[72] !== t40 || $[73] !== t41) {
    t42 = <div className={t29}>{t30}{t40}{t41}</div>;
    $[70] = t29;
    $[71] = t30;
    $[72] = t40;
    $[73] = t41;
    $[74] = t42;
  } else {
    t42 = $[74];
  }
  let t43;
  if ($[75] !== t27.Provider || $[76] !== t28 || $[77] !== t42) {
    t43 = <t27.Provider value={t28}>{t42}</t27.Provider>;
    $[75] = t27.Provider;
    $[76] = t28;
    $[77] = t42;
    $[78] = t43;
  } else {
    t43 = $[78];
  }
  let t44;
  if ($[79] !== readOnly || $[80] !== t26.Provider || $[81] !== t43) {
    t44 = <t26.Provider value={readOnly}>{t43}</t26.Provider>;
    $[79] = readOnly;
    $[80] = t26.Provider;
    $[81] = t43;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== mentionContext || $[84] !== t25.Provider || $[85] !== t44) {
    t45 = <t25.Provider value={mentionContext}>{t44}</t25.Provider>;
    $[83] = mentionContext;
    $[84] = t25.Provider;
    $[85] = t44;
    $[86] = t45;
  } else {
    t45 = $[86];
  }
  return t45;
};
export default ChatPanel;
function _temp(c) {
  return !c;
}
function _temp2(err) {
  return console.error("[agent-chat] segment render error", err);
}
function _temp3($) {
  return $.chat.envLocked;
}
function _temp4(m_0) {
  return m_0.type === "turn_complete";
}
function _temp5(n, m_1) {
  return m_1.type === "turn_complete" ? n + 1 : n;
}
function _temp6(t0) {
  const {
    msg
  } = t0;
  return msg;
}
function _temp7(q) {
  return !!q.options && q.options.length > 0;
}
function _temp8(t_1) {
  return t_1.status !== "completed";
}
function _temp9(m_4) {
  return !m_4.question_id;
}
function _temp10($_0) {
  return $_0.chat.disabledDuringInvestigationPlaceholder;
}
function _temp11($_1) {
  return $_1.chat.answerQuestion;
}
function _temp12($_2) {
  return $_2.chat.answerHere;
}
function _temp13($_3) {
  return $_3.chat.startNewConversationPlaceholder;
}
function _temp14($_4) {
  return $_4.chat.replyPlaceholder;
}
function _temp15(q_0) {
  return !!q_0.options && q_0.options.length > 0;
}
function _temp16(q_1) {
  return !!q_1.options && q_1.options.length > 0;
}
function _temp17(q_2) {
  return !!q_2.options && q_2.options.length > 0;
}
function _temp18($_5) {
  return $_5.chat.investigationEndedMarker;
}